import { Component, OnInit, Input, ViewChild } from '@angular/core';
import { PagedResultGeneric, DatagridService, TranslationService, NotificationService } from 'src/app/shared';
import { CommitteeMemberDto } from 'src/app/committees/shared/dto/committee-member.dto';
import { CommitteeMemberSearchDto } from 'src/app/committees/shared/dto/committee-member-search.dto';
import { MenuItem, SelectItem } from 'primeng/api';
import { CommitteeMemberService } from 'src/app/committees/shared/committee-member.service';
import { LazySelectableTableComponent } from 'src/app/shared/primeng/abstract-classes/lazy-selectable-table.component';
import { ActivatedRoute } from '@angular/router';
import { CommitteeMemberEditDialogComponent } from 'src/app/committees/committee-member-edit-dialog/committee-member-edit-dialog.component';
import { SecurityElementNames } from 'src/app/shared/auth/constants/security-element-names.service';
import { NavigationService } from 'src/app/shared/navigation/navigation.service';

@Component({
  selector: 'wl-committee-members-search-grid',
  templateUrl: './committee-members-search-grid.component.html',
  styleUrls: ['./committee-members-search-grid.component.less'],
    standalone: false
})
export class CommitteeMembersSearchGridComponent
  extends LazySelectableTableComponent<CommitteeMemberSearchDto, CommitteeMemberDto>
  implements OnInit {
  @ViewChild(CommitteeMemberEditDialogComponent) _memberCrudDialog: CommitteeMemberEditDialogComponent;

  rowMenuItems: Map<number, MenuItem[]> = new Map();
  pagingSizeItems: SelectItem[] = [];
  exporting = false;

  constructor(
    _datagridService: DatagridService,
    public securityElementNames: SecurityElementNames,
    public route: ActivatedRoute,
    private _committeeMemberService: CommitteeMemberService,
    private _translationService: TranslationService,
    private _notificationService: NotificationService,
    private _navigationService: NavigationService
  ) {
    super('CommitteeMemberID', _datagridService);
  }

  async ngOnInit(): Promise<void> {
    this.pagingSizeItems = [
      {
        label: await this._translationService.getTitlecase('common.page-size.show-20'),
        value: 20,
      },
      {
        label: await this._translationService.getTitlecase('common.page-size.show-100'),
        value: 100,
      },
      {
        label: await this._translationService.getTitlecase('common.page-size.show-all'),
        value: 9999,
      },
    ];
  }

  fetchResults(): Promise<PagedResultGeneric<CommitteeMemberDto>> {
    return this._committeeMemberService.search(this.searchParams);
  }

  clearSearch() {
    const committeeId = this.searchParams.CommitteeIds.find((x) => true);
    this.searchParams = new CommitteeMemberSearchDto();
    this.searchParams.CommitteeIds = [committeeId];
    this.search();
  }

  actionItemsForMember(member: CommitteeMemberDto): MenuItem[] {
    if (!this.rowMenuItems.has(member.CommitteeMemberID)) {
      let activateLabel = this._translationService.getInstantTitlecase('committees.edit.committee-members.inactivate');
      if (!member.Active) {
        activateLabel = this._translationService.getInstantTitlecase('committees.edit.committee-members.activate');
      }

      this.rowMenuItems.set(member.CommitteeMemberID, [
        {
          label: this._translationService.getInstantTitlecase('committees.edit.committee-members.edit'),
          command: () => this.editMember(member),
        },
        {
          label: activateLabel,
          command: () => this.setMemberActive(member, !member.Active),
        },
        {
          label: this._translationService.getInstantTitlecase('committees.edit.committee-members.delete'),
          command: () => this.deleteMember(member),
        },
      ]);
    }
    return this.rowMenuItems.get(member.CommitteeMemberID);
  }

  setMemberActive(member: CommitteeMemberDto, newValue: boolean) {
    member.Active = newValue;
    this._committeeMemberService
      .update(member)
      .then(() => {
        this._notificationService.translateSuccess('committees.edit.committee-members.member-updated-successfully');

        this.search();
      })
      .catch((err) => {
        this._notificationService.httpError(err);
        member.Active = !newValue;
      });
  }

  pageSizeChanged() {
    this.searchParams.PageNumber = 1;
    this.search();
  }

  exportCommitteeMembers() {
    this.exporting = true;
    this._committeeMemberService
      .searchExportToCsv(this.searchParams)
      .then((csvUrl) => {
        this._navigationService.popupSafeNavigate(csvUrl);
      })
      .catch((err) => {
        this._notificationService.httpError(err);
      })
      .finally(() => {
        this.exporting = false;
      });
  }

  private editMember(member: CommitteeMemberDto) {
    this._memberCrudDialog.open(member.CommitteeMemberID);
  }

  private deleteMember(member: CommitteeMemberDto) {
    this._committeeMemberService.delete(member.CommitteeMemberID).then(() => {
      this.search();
    });
  }

  public resultsUpdated() {
    this.reset();
    this.search();
  }
}
